import {Component, Input, OnInit} from '@angular/core';
import {Producto} from '../../../../_models/producto';
import {NavService} from '../../../../_services/_nav.service';
import {ConfirmationService} from 'primeng/api';
import {AbstractControl, FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {ProductoMontoPlazoInversionService} from '../../../../_services/producto-monto-plazo-inversion.service';
import {ProductoMontoPlazoInversion} from '../../../../_models/producto-monto-plazo-inversion';
import {switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-producto-montos-plazos',
  templateUrl: './producto-montos-plazos.component.html',
  styleUrl: './producto-montos-plazos.component.css'
})
export class ProductoMontosPlazosComponent implements OnInit {
  @Input() producto: Producto;
  @Input() isDetail: boolean = false;
  loading: boolean = true;
  productoMontosPlazos: ProductoMontoPlazoInversion[] = [];
  submitted: boolean = false;
  saving: boolean = false;
  form: FormGroup;
  cols: any[] = [];
  rows: any[] = [];

  constructor(
    private productoMontoPlazoInversionService: ProductoMontoPlazoInversionService,
    private navServicio: NavService,
    private confirmationService: ConfirmationService,
    protected fb: FormBuilder,
  ) {
  }

  ngOnInit(): void {
    this.productoMontoPlazoInversionService.getByIdProducto(this.producto.idProducto).subscribe({
      next: (data: ProductoMontoPlazoInversion[]) => {
        this.productoMontosPlazos = data;
        this.loadForm();
        this.loading = false;
      },
    });
  }

  loadForm(): void {
    this.submitted = false;

    this.form = this.fb.group({
      montosPlazos: this.fb.array(
        this.productoMontosPlazos.map(item =>
          this.fb.group({
            id: [item.id],
            idProductoPlazoMontoInversion: [item.idProductoPlazoMontoInversion],
            idProductoMontoInversion: [item.idProductoMontoInversion],
            idProductoPlazoInversion: [item.idProductoPlazoInversion],
            valor: [item.valor],
            rangoInicialPlazosInversion: [item.rangoInicialPlazosInversion],
            rangoFinalPlazosInversion: [item.rangoFinalPlazosInversion],
            rangoInicialMontoInversion: [item.rangoInicialMontoInversion],
            rangoFinalMontoInversion: [item.rangoFinalMontoInversion],
            indicadorHabilitado: [item.indicadorHabilitado],
            auditoriaFecha: [item.auditoriaFecha],
            auditoriaUsuario: [item.auditoriaUsuario],
            ip: [item.ip],
            _valorOriginal: [item.valor]
          })
        )
      ),
    });

    this.createMatrix();
  }

  get f(): { [key: string]: AbstractControl | null } {
    return this.form.controls;
  }

  get fMontosPlazos(): FormArray {
    return this.f.montosPlazos as FormArray;
  }

  fMontoPlazo(index: number, controlName: string): AbstractControl | null {
    return this.fMontosPlazos.at(index)?.get(controlName) || null;
  }

  hasIndex(monto: any, plazo: any): boolean {
    return this.getIndexMontoPlazo(monto, plazo) !== null;
  }

  getIndexMontoPlazo(monto: any, plazo: any): number | null {
    const index: number = this.productoMontosPlazos.findIndex(item =>
      item.rangoInicialMontoInversion === monto.rangoInicial &&
      item.rangoFinalMontoInversion === monto.rangoFinal &&
      item.rangoInicialPlazosInversion === plazo.rangoInicial &&
      item.rangoFinalPlazosInversion === plazo.rangoFinal
    );
    return index !== -1 ? index : null;
  }

  createMatrix(): void {
    const plazosMap = new Map();
    this.productoMontosPlazos.forEach(item => {
      const key = `${item.rangoInicialPlazosInversion}-${item.rangoFinalPlazosInversion}`;
      if (!plazosMap.has(key)) {
        plazosMap.set(key, {
          rangoInicial: item.rangoInicialPlazosInversion,
          rangoFinal: item.rangoFinalPlazosInversion
        });
      }
    });
    this.cols = Array.from(plazosMap.values());

    const montosMap = new Map();
    this.productoMontosPlazos.forEach(item => {
      const key = `${item.rangoInicialMontoInversion}-${item.rangoFinalMontoInversion}`;
      if (!montosMap.has(key)) {
        montosMap.set(key, {
          rangoInicial: item.rangoInicialMontoInversion,
          rangoFinal: item.rangoFinalMontoInversion
        });
      }
    });
    this.rows = Array.from(montosMap.values());
  }

  onCancel(): void {
    if (this.form.dirty) {
      this.confirmationService.confirm({
        message: this.navServicio.buscarMensaje('MSG_CANCELARCAMBIOS'),
        header: this.navServicio.buscarMensaje('LBL_CONFIRMACION'),
        icon: 'fa fa-question-circle',
        key: 'producto-montos-plazos-inversion',
        accept: () => this.loadForm(),
      });
    } else {
      this.loadForm();
    }
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) return;

    this.saving = true;
    const data: ProductoMontoPlazoInversion[] = this.form.value?.montosPlazos;
    const modifiedData: ProductoMontoPlazoInversion[] = data.filter((e: ProductoMontoPlazoInversion, i: number) => {
      const valorActual: number = e.valor;
      const valorOriginal = this.fMontoPlazo(i, '_valorOriginal')?.value;

      return valorActual !== Number(valorOriginal);
    });

    if (modifiedData.length === 0) {
      this.navServicio.setMesage(0, {
        severity: 'info',
        summary: this.navServicio.buscarMensaje( 'LBL_ADVERTENCIA' ),
        detail: this.navServicio.buscarMensaje( 'PRODUCTO.MSG_SIN_CAMBIOS' )
      });
      this.saving = false;
      return;
    }

    this.productoMontoPlazoInversionService.bulk(modifiedData).pipe(switchMap(() => this.productoMontoPlazoInversionService.getByIdProducto(this.producto.idProducto))).subscribe({
      next: (response: ProductoMontoPlazoInversion[]) => {
        this.navServicio.setMesage(1);
        this.productoMontosPlazos = response;
        this.loadForm();
        this.saving = false;
      },
      error: (_) => {
        this.navServicio.setMesage(3);
        this.saving = false;
      }
    });
  }

  onBlurValor(index: number): void {
    const valorControl = this.fMontoPlazo(index, 'valor');

    if (!valorControl?.value) {
      valorControl?.setValue(0);
    }
  }

}
